/**
 * Language Display Helpers
 *
 * Utilities for formatting language information in classification results
 */

/**
 * Language code to display name mapping
 */
const LANGUAGE_MAP: Record<string, string> = {
  aa: 'Afar',
  ab: 'Abkhazian',
  af: 'Afrikaans',
  am: 'Amharic',
  ar: 'Arabic',
  as: 'Assamese',
  ay: 'Aymara',
  az: 'Azerbaijani',
  ba: 'Bashkir',
  be: 'Byelorussian',
  bg: 'Bulgarian',
  bh: 'Bihari',
  bi: 'Bislama',
  bn: 'Bengali, Bangla',
  bo: 'Tibetan',
  br: 'Breton',
  ca: 'Catalan',
  co: 'Corsican',
  cs: 'Czech',
  cy: 'Welsh',
  da: 'Danish',
  de: 'German',
  dz: 'Bhutani',
  el: 'Greek',
  en: 'English',
  eo: 'Esperanto',
  es: 'Spanish',
  et: 'Estonian',
  eu: 'Basque',
  fa: 'Persian',
  fi: 'Finnish',
  fj: 'Fiji',
  fo: 'Faeroese',
  fr: 'French',
  fy: 'Frisian',
  ga: 'Irish',
  gd: 'Gaelic (Scots Gaelic)',
  gl: 'Galician',
  gn: 'Guarani',
  gu: 'Gujarati',
  ha: 'Hausa',
  hi: 'Hindi',
  hr: 'Croatian',
  hu: 'Hungarian',
  hy: 'Armenian',
  ia: 'Interlingua',
  ie: 'Interlingue',
  ik: 'Inupiak',
  in: 'Indonesian',
  is: 'Icelandic',
  it: 'Italian',
  iw: 'Hebrew',
  ja: 'Japanese',
  ji: 'Yiddish',
  jw: 'Javanese',
  ka: 'Georgian',
  kk: 'Kazakh',
  kl: 'Greenlandic',
  km: 'Cambodian',
  kn: 'Kannada',
  ko: 'Korean',
  ks: 'Kashmiri',
  ku: 'Kurdish',
  ky: 'Kirghiz',
  la: 'Latin',
  ln: 'Lingala',
  lo: 'Laothian',
  lt: 'Lithuanian',
  lv: 'Latvian, Lettish',
  mg: 'Malagasy',
  mi: 'Maori',
  mk: 'Macedonian',
  ml: 'Malayalam',
  mn: 'Mongolian',
  mo: 'Moldavian',
  mr: 'Marathi',
  ms: 'Malay',
  mt: 'Maltese',
  my: 'Burmese',
  na: 'Nauru',
  ne: 'Nepali',
  nl: 'Dutch',
  no: 'Norwegian',
  oc: 'Occitan',
  om: 'Oromo, Afan',
  or: 'Oriya',
  pa: 'Punjabi',
  pl: 'Polish',
  ps: 'Pashto, Pushto',
  pt: 'Portuguese',
  qu: 'Quechua',
  rm: 'Rhaeto-Romance',
  rn: 'Kirundi',
  ro: 'Romanian',
  ru: 'Russian',
  rw: 'Kinyarwanda',
  sa: 'Sanskrit',
  sd: 'Sindhi',
  sg: 'Sangro',
  sh: 'Serbo-Croatian',
  si: 'Singhalese',
  sk: 'Slovak',
  sl: 'Slovenian',
  sm: 'Samoan',
  sn: 'Shona',
  so: 'Somali',
  sq: 'Albanian',
  sr: 'Serbian',
  ss: 'Siswati',
  st: 'Sesotho',
  su: 'Sudanese',
  sv: 'Swedish',
  sw: 'Swahili',
  ta: 'Tamil',
  te: 'Tegulu',
  tg: 'Tajik',
  th: 'Thai',
  ti: 'Tigrinya',
  tk: 'Turkmen',
  tl: 'Tagalog',
  tn: 'Setswana',
  to: 'Tonga',
  tr: 'Turkish',
  ts: 'Tsonga',
  tt: 'Tatar',
  tw: 'Twi',
  uk: 'Ukrainian',
  ur: 'Urdu',
  uz: 'Uzbek',
  vi: 'Vietnamese',
  vo: 'Volapuk',
  wo: 'Wolof',
  xh: 'Xhosa',
  yo: 'Yoruba',
  zh: 'Chinese',
  zu: 'Zulu',
};

export interface LanguageDisplayInfo {
  /** Primary display text for the language */
  displayText: string;
  /** Secondary text to show (e.g., translation indicator) */
  secondaryText?: string;
  /** Whether translation occurred */
  showTranslationIndicator: boolean;
  /** Tooltip text with more details */
  tooltipText?: string;
}

/**
 * Get human-readable language name from language code
 */
export function getLanguageName(languageCode: string | undefined): string {
  if (!languageCode) {
    return 'Unknown';
  }

  const lowerCode = languageCode.toLowerCase();

  return LANGUAGE_MAP[lowerCode] || languageCode.toUpperCase();
}

/**
 * Get display information for language in classification results
 */
export function getLanguageDisplayInfo(
  languageDetected: string | undefined,
  languageClassified: string | undefined,
  _wasTranslated?: boolean
): LanguageDisplayInfo {
  const detectedName = getLanguageName(languageDetected);
  const classifiedName = getLanguageName(languageClassified);

  // If no language detected, return unknown
  if (!languageDetected) {
    return {
      displayText: 'Unknown',
      showTranslationIndicator: false,
      tooltipText: 'Language: Unknown',
    };
  }

  // If no classified language or same as detected, show simple display
  if (!languageClassified || languageDetected.toLowerCase() === languageClassified.toLowerCase()) {
    return {
      displayText: detectedName,
      showTranslationIndicator: false,
      tooltipText: `Detected Language: ${detectedName}`,
    };
  }

  // Different languages detected vs classified - show both
  return {
    displayText: `${detectedName}`,
    secondaryText: `Classified: ${classifiedName}`,
    showTranslationIndicator: true,
    tooltipText: `Content detected in ${detectedName}, classified in ${classifiedName} after translation`,
  };
}

/**
 * Check if language information indicates translation occurred
 */
export function didTranslationOccur(languageDetected: string | undefined, languageClassified: string | undefined, wasTranslated?: boolean): boolean {
  // Explicit flag if provided
  if (wasTranslated !== undefined) {
    return wasTranslated;
  }

  // Otherwise infer from language codes
  if (!languageDetected || !languageClassified) {
    return false;
  }

  return languageDetected.toLowerCase() !== languageClassified.toLowerCase();
}
